/**
 * PlatformCommercialPage — the Platform Operator's agency LIST.
 *
 * Task 298 (Foundation F). Specification:
 * `tasks/295A-commercial-foundation-and-staging.md` §6.3, §8.1, §9.3.
 * Task 306A §1.5 gave the list read its generation token.
 * Task 307A rebuilds the screen to the approved design direction and
 * moves the decision itself to a route. Task file:
 * `tasks/307A-platform-operator-console.md`.
 *
 * ---------------------------------------------------------------------
 * PLATFORM-ONLY, AND IT MUST NOT RENDER FOR A TENANT ACTOR (295A §9.3).
 *
 * The gate here is `me.system_admin` — the SAME direct platform-role fact
 * the server enforces — and NOT a permission key. There is deliberately
 * no `platform` permission key in this system: `app.has_permission()`
 * short-circuits to false whenever `target_org_id` is null and a platform
 * operator's `app.current_org_id()` IS null, so a platform-scoped
 * permission is unresolvable by the standard helper. Seeding one would
 * create the APPEARANCE of a control, which is worse than nothing
 * (295A §6.2).
 *
 * THE UI IS NOT THE CONTROL. Every endpoint authorizes independently, as
 * the first statement of its definer RPC or as the first statement after
 * authentication. Hiding this page from a tenant actor is UX; the server
 * refusing them is the security property.
 *
 * ---------------------------------------------------------------------
 * TASK 307A — WHAT CHANGED, AND WHY THE ROW NO LONGER TRANSACTS.
 *
 * 306A §1.4 made the detail an in-row disclosure and gave the row its own
 * action block, on the argument that navigating away would lose the
 * filter and the typed reason. Both halves of that argument are now
 * answered rather than dismissed: the query lives in the URL, so the
 * filter survives the round trip and the "all agencies" link comes back
 * to the exact page the operator left; and the reason is typed in the
 * decision dialog on the detail route, where the evidence being judged is
 * on the same screen as the decision.
 *
 * What is left is that two ways to commit one transition is a defect
 * class, not a feature — so this screen NAVIGATES and does not transact.
 * There is no action button, no reason input and no confirmation here.
 *
 * SEARCH, FILTER, SORT AND PAGINATION ARE ALL SERVER-SIDE, and they live
 * in the address bar. Filtering the page that arrived would be a lie the
 * moment the result set exceeds one page, which is exactly what `total`
 * reports. The round trip through `parseOperatorListQuery` /
 * `operatorListQueryToSearch` is what makes refresh and Back/Forward
 * work, and it is executed by test rather than asserted here.
 *
 * NOTHING CLIENT-SCOPED IS SHOWN. The row carries an aggregate client
 * count and no client id, name or status: the endpoint returns none, and
 * this page asks for nothing else.
 * ---------------------------------------------------------------------
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { listOrganizationCommercialState } from '../../api/endpoints';
import { COMMERCIAL_LIST_SORTS, COMMERCIAL_STATES } from '../../api/types';
import type {
  CommercialListPlan,
  CommercialListSort,
  CommercialState,
  OrganizationCommercialStateRow,
} from '../../api/types';
import { useMeContext } from '../../context/MeContextProvider';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PermissionDeniedState,
} from '../../components/states';
import { useLatestRef } from './useLatestRef';
import {
  COMMERCIAL_STATE_LABELS_AR,
  DEFAULT_OPERATOR_LIST_QUERY,
  LEDGER_ACTION_LABELS_AR,
  LIST_SORT_LABELS_AR,
  MAX_OPERATOR_SEARCH_LENGTH,
  agencyDetailPath,
  clampPage,
  completionPercent,
  createLatestRequest,
  displayValue,
  formatOperatorTimestamp,
  needsDecision,
  operatorListQueryToParams,
  operatorListQueryToSearch,
  outOfRangePageRedirect,
  pageCount,
  parseOperatorListQuery,
  planLabel,
  runCommercialListLoad,
} from './operatorConsole';
import type { LatestRequest, OperatorListQuery } from './operatorConsole';

/** The segmented standing filter, in the design's reading order. */
const STATE_SEGMENTS: ReadonlyArray<{
  readonly value: CommercialState | '';
  readonly label: string;
}> = [
  { value: '', label: 'الكل' },
  ...COMMERCIAL_STATES.map((state) => ({
    value: state,
    label: COMMERCIAL_STATE_LABELS_AR[state],
  })),
];

export function PlatformCommercialPage() {
  const { me } = useMeContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const query = useMemo(() => parseOperatorListQuery(searchParams), [searchParams]);

  const [rows, setRows] = useState<ReadonlyArray<OrganizationCommercialStateRow>>([]);
  const [total, setTotal] = useState(0);
  const [plans, setPlans] = useState<ReadonlyArray<CommercialListPlan>>([]);
  const [pendingTotal, setPendingTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  /**
   * The search box as TYPED, which is not the search that is APPLIED.
   *
   * Pushing a history entry per keystroke would make Back walk backwards
   * through half-typed words, and issuing a request per keystroke would
   * put a search of one character in front of every operator. The draft
   * is committed on submit; the URL is the applied value.
   */
  const [searchDraft, setSearchDraft] = useState(query.search);

  // One generation counter for the list read. A ref, not state: it must
  // not cause a render, and it must survive one. Lazily built, because
  // `useRef(create…())` rebuilds and discards a counter on every render.
  const latestListRef = useRef<LatestRequest | null>(null);
  if (latestListRef.current === null) latestListRef.current = createLatestRequest();
  const latestList = latestListRef.current;

  /**
   * The query as of the latest COMMITTED render — 306A §1.5.1/§1.5.2.
   *
   * Everything the loader reads after its `await` must be the live value
   * rather than one render's, and it must be one that actually reached
   * the screen: a render React started and threw away must publish
   * nothing. `useLatestRef` publishes from a layout effect for exactly
   * that reason.
   */
  const queryRef = useLatestRef(query);

  /**
   * Reload the list under the query that is live NOW.
   *
   * The token is taken in the same tick as `readQuery()`, so the request
   * holding the newest token is by construction the request for the query
   * that was live when it was taken. Every write after the `await` is
   * guarded on all three settlement paths — an older answer must not put
   * another page's rows under this page's heading, must not report a
   * failure the operator abandoned, and must not drop the spinner while
   * the read they are waiting on is still open.
   */
  const load = useCallback(
    (): Promise<void> =>
      runCommercialListLoad(latestList, () => operatorListQueryToParams(queryRef.current), {
        fetchList: listOrganizationCommercialState,
        setRows,
        setTotal,
        setExtras: (extras) => {
          setPlans(extras.plans);
          setPendingTotal(extras.pendingTotal);
        },
        setError,
        setLoading,
      }).fresh,
    [latestList, queryRef],
  );

  useEffect(() => {
    if (!me?.system_admin) return;
    void load();
  }, [load, me?.system_admin, query]);

  // The typed draft follows the applied query when the query changes for
  // any reason the operator did not type — Back, Forward, or a refresh.
  useEffect(() => {
    setSearchDraft(query.search);
  }, [query.search]);

  const applyQuery = useCallback(
    (next: OperatorListQuery, options?: { readonly replace?: boolean }) => {
      setSearchParams(operatorListQueryToSearch(next), { replace: options?.replace ?? false });
    },
    [setSearchParams],
  );

  /**
   * The URL asked for a page the result set no longer has — 307A.
   *
   * The endpoint recovers the authoritative `total` on exactly this path
   * (it re-reads the two page-independent windows at offset zero rather
   * than reporting a fabricated 0), so the console can tell "past the
   * end" apart from "nothing matches" and put the operator on the last
   * real page instead of on an empty screen with no pager.
   *
   * `replace`, so Back does not bounce the operator straight back onto
   * the page that does not exist. Changing the query re-runs the load
   * effect above, which is the re-read.
   */
  useEffect(() => {
    const redirect = outOfRangePageRedirect({
      page: query.page,
      total,
      rowCount: rows.length,
      loading,
      failed: error !== null,
    });
    if (redirect === null) return;
    applyQuery({ ...query, page: redirect }, { replace: true });
  }, [applyQuery, error, loading, query, rows.length, total]);

  // Platform-only. A tenant actor never renders this page — and every
  // endpoint behind it refuses them regardless.
  if (!me) return <LoadingState title="جاري تحميل الجلسة…" />;
  if (!me.system_admin) return <PermissionDeniedState />;

  const pages = pageCount(total);
  const currentPage = clampPage(query.page, total);
  const listRendered = !loading && !error;
  const filtersActive =
    query.state !== '' || query.planKey !== '' || query.search !== '' || query.page > 0;

  return (
    <div className="page op-console" dir="rtl" data-testid="platform-commercial-page">
      <header className="page-head">
        <div>
          <h1>
            الوكالات <em className="op-accent">التجارية</em>
          </h1>
          <p className="sub">
            كل وكالة مسجّلة في المنصة، وحالتها التجارية. التفعيل قرار يدوي: افتح الوكالة لمراجعة
            ملفها النظامي قبل اتخاذ القرار.
          </p>
        </div>
      </header>

      {pendingTotal !== null ? (
        <section className="op-band" data-testid="op-decision-band">
          <div className="op-band-copy">
            <h2>يحتاج قراراً الآن</h2>
            <p>وكالات سجّلت وتنتظر قرار التفعيل. لا يمكن لأي منها استخدام المنتج قبل ذلك.</p>
          </div>
          <div className="op-band-stat">
            <span className="v" data-testid="op-pending-total">
              {pendingTotal}
            </span>
            <span className="k">بانتظار التنشيط</span>
          </div>
          {pendingTotal > 0 ? (
            <button
              type="button"
              className="btn btn-accent"
              data-testid="op-review-pending"
              onClick={() =>
                applyQuery({
                  ...DEFAULT_OPERATOR_LIST_QUERY,
                  state: 'pending_activation',
                  sort: 'least_complete',
                })
              }
            >
              مراجعة الطلبات المعلّقة
            </button>
          ) : null}
        </section>
      ) : null}

      <section className="op-filters" data-testid="op-filters">
        <form
          className="search op-search"
          role="search"
          onSubmit={(event) => {
            event.preventDefault();
            applyQuery({ ...query, search: searchDraft.trim(), page: 0 });
          }}
        >
          <input
            type="search"
            aria-label="بحث بالاسم أو الاسم النظامي أو السجل التجاري"
            placeholder="ابحث بالاسم أو السجل التجاري…"
            maxLength={MAX_OPERATOR_SEARCH_LENGTH}
            data-testid="op-search-input"
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
          />
        </form>

        <div className="op-seg" role="group" aria-label="تصفية حسب الحالة التجارية">
          {STATE_SEGMENTS.map((segment) => (
            <button
              key={segment.value || 'all'}
              type="button"
              className={`op-seg-item${query.state === segment.value ? ' is-active' : ''}`}
              aria-pressed={query.state === segment.value}
              data-testid={`op-state-${segment.value || 'all'}`}
              onClick={() => applyQuery({ ...query, state: segment.value, page: 0 })}
            >
              {segment.label}
            </button>
          ))}
        </div>

        <label className="op-select">
          <span className="tiny">الخطة</span>
          <select
            data-testid="op-plan-filter"
            value={query.planKey}
            onChange={(event) => applyQuery({ ...query, planKey: event.target.value, page: 0 })}
          >
            <option value="">كل الخطط</option>
            {plans.map((plan) => (
              <option key={plan.key} value={plan.key}>
                {planLabel(plan.key, plan.name_ar)}
              </option>
            ))}
          </select>
        </label>

        <label className="op-select">
          <span className="tiny">الترتيب</span>
          <select
            data-testid="op-sort"
            value={query.sort}
            onChange={(event) =>
              applyQuery({ ...query, sort: event.target.value as CommercialListSort, page: 0 })
            }
          >
            {COMMERCIAL_LIST_SORTS.map((sort) => (
              <option key={sort} value={sort}>
                {LIST_SORT_LABELS_AR[sort]}
              </option>
            ))}
          </select>
        </label>

        <p className="tiny op-result-count" data-testid="op-result-count">
          {loading ? 'جاري التحميل…' : `${total} وكالة`}
        </p>
      </section>

      {loading ? <LoadingState title="جاري تحميل قائمة الوكالات…" /> : null}
      {error ? (
        <ErrorState
          title="تعذّر تحميل قائمة الوكالات"
          description="لم يُفقد أي شيء ولم يتغيّر أي سجل. حاول مرة أخرى."
          error={error}
          onRetry={() => void load()}
        />
      ) : null}

      {listRendered && rows.length === 0 ? (
        <EmptyState
          title="لا توجد وكالات مطابقة"
          description={
            filtersActive
              ? 'جرّب توسيع البحث أو إزالة التصفية.'
              : 'لم تُسجَّل أي وكالة في المنصة بعد.'
          }
          action={
            filtersActive ? (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                data-testid="op-clear-filters"
                onClick={() => applyQuery(DEFAULT_OPERATOR_LIST_QUERY)}
              >
                إلغاء التصفية
              </button>
            ) : undefined
          }
        />
      ) : null}

      {listRendered && rows.length > 0 ? (
        <>
          <div className="card op-table-wrap">
            <table className="tbl op-table">
              <thead>
                <tr>
                  <th>الوكالة</th>
                  <th>الوضع التجاري</th>
                  <th>الخطة</th>
                  <th>اكتمال الملف</th>
                  <th className="num">العملاء</th>
                  <th>تاريخ التسجيل</th>
                  <th>آخر قرار</th>
                  <th aria-label="فتح" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <OrganizationRow
                    key={row.organization_id}
                    row={row}
                    onOpen={() =>
                      navigate({
                        pathname: agencyDetailPath(row.organization_id),
                        search: operatorListQueryToSearch(query),
                      })
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* The same rows again for narrow viewports. `.op-table-wrap`
              and `.op-cards` are each hidden by the other's breakpoint, so
              only one is ever painted — the design's table/record-card
              split, not two independent lists that could disagree. */}
          <ul className="op-cards" data-testid="op-cards">
            {rows.map((row) => (
              <OrganizationCard
                key={row.organization_id}
                row={row}
                to={{
                  pathname: agencyDetailPath(row.organization_id),
                  search: operatorListQueryToSearch(query),
                }}
              />
            ))}
          </ul>

          <nav className="op-pager" aria-label="تنقّل بين الصفحات" data-testid="op-pager">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={currentPage <= 0}
              data-testid="op-page-prev"
              onClick={() => applyQuery({ ...query, page: Math.max(0, currentPage - 1) })}
            >
              السابق
            </button>
            <span className="tiny" data-testid="op-page-indicator">
              صفحة {currentPage + 1} من {pages}
            </span>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={currentPage >= pages - 1}
              data-testid="op-page-next"
              onClick={() => applyQuery({ ...query, page: Math.min(pages - 1, currentPage + 1) })}
            >
              التالي
            </button>
          </nav>
        </>
      ) : null}
    </div>
  );
}

/**
 * One agency, as a table row — EXPORTED AND PROP-DRIVEN.
 *
 * Rendered directly by test rather than reached through the page's data
 * loading, for the reason 306A §1.4 exported its detail panel: this
 * repository has no DOM test environment, and a surface nobody can mount
 * is a surface nobody verifies.
 *
 * The WHOLE ROW opens the detail (the design's rule), and it does so as a
 * real navigation rather than a state swap. A `<tr>` cannot be a link, so
 * the row carries the click and the last cell carries a focusable control
 * with an accessible name — a row that only a mouse can open is not one
 * every operator can open.
 */
export function OrganizationRow({
  row,
  onOpen,
}: {
  readonly row: OrganizationCommercialStateRow;
  readonly onOpen: () => void;
}) {
  const decision = needsDecision(row);
  return (
    <tr
      className={`op-row${decision ? ' needs-decision' : ''}`}
      data-testid={`org-row-${row.organization_id}`}
      onClick={onOpen}
    >
      <td>
        <span className="tbl name">{row.organization_name}</span>
        <span className="tbl sub">
          <bdi>{[row.legal_name, row.city].filter(Boolean).join(' · ') || '—'}</bdi>
        </span>
        <span className="tbl sub">
          السجل التجاري: <bdi>{displayValue(row.commercial_registration_no)}</bdi>
        </span>
      </td>
      <td data-testid={`org-state-${row.organization_id}`}>
        <StandingPill state={row.commercial_state} />
      </td>
      <td>{planLabel(row.plan_key, row.plan_name_ar)}</td>
      <td>
        <CompletionMeter filled={row.profile_filled} total={row.profile_total} />
      </td>
      <td className="num">{row.client_count}</td>
      <td dir="ltr" className="op-ltr">
        {formatOperatorTimestamp(row.created_at)}
      </td>
      <td>
        {row.last_decision_action ? (
          <>
            <span>
              {LEDGER_ACTION_LABELS_AR[row.last_decision_action] ?? row.last_decision_action}
            </span>
            <span className="tbl sub" dir="ltr">
              {formatOperatorTimestamp(row.last_decision_at)}
            </span>
          </>
        ) : (
          '—'
        )}
      </td>
      <td>
        <button
          type="button"
          className="btn btn-text btn-sm"
          data-testid={`org-open-${row.organization_id}`}
          onClick={(event) => {
            event.stopPropagation();
            onOpen();
          }}
        >
          {`مراجعة ${row.organization_name}`}
        </button>
      </td>
    </tr>
  );
}

/** The same agency as a card, for `≤980px`. Exported for the same reason. */
export function OrganizationCard({
  row,
  to,
}: {
  readonly row: OrganizationCommercialStateRow;
  readonly to: { readonly pathname: string; readonly search: string };
}) {
  return (
    <li className={`card op-card${needsDecision(row) ? ' needs-decision' : ''}`}>
      <Link className="op-card-link" to={to} data-testid={`org-card-${row.organization_id}`}>
        <span className="op-card-head">
          <span className="tbl name">{row.organization_name}</span>
          <StandingPill state={row.commercial_state} />
        </span>
        <span className="tbl sub">
          <bdi>{displayValue(row.legal_name)}</bdi>
        </span>
        <span className="op-card-kv">
          <span className="tiny">الخطة</span>
          <span>{planLabel(row.plan_key, row.plan_name_ar)}</span>
          <span className="tiny">السجل التجاري</span>
          <span>
            <bdi>{displayValue(row.commercial_registration_no)}</bdi>
          </span>
          <span className="tiny">اكتمال الملف</span>
          <span>
            {row.profile_filled} / {row.profile_total}
          </span>
          <span className="tiny">العملاء</span>
          <span>{row.client_count}</span>
          <span className="tiny">التسجيل</span>
          <span dir="ltr" className="op-ltr">
            {formatOperatorTimestamp(row.created_at)}
          </span>
        </span>
      </Link>
    </li>
  );
}

/**
 * The standing, named and coloured. Never a raw enum in presentation.
 *
 * The pill class carries the meaning as well as the colour, so a reader
 * who cannot distinguish sage from amber still reads the word.
 */
export function StandingPill({ state }: { readonly state: CommercialState }) {
  const tone =
    state === 'activated'
      ? 'pill-active'
      : state === 'pending_activation'
        ? 'pill-review'
        : state === 'suspended'
          ? 'pill-error'
          : 'pill-archived';
  return (
    <span className={`pill ${tone}`} data-testid={`op-pill-${state}`}>
      {COMMERCIAL_STATE_LABELS_AR[state]}
    </span>
  );
}

/**
 * Completion over the SIX required fields, as a meter and as a number.
 *
 * The number is printed beside the bar rather than encoded only in its
 * width: a bar is a comparison, and "is this ready to activate" is a
 * count. `aria-valuetext` names the same two numbers, so the meter is not
 * a fact only sighted operators have.
 */
export function CompletionMeter({
  filled,
  total,
}: {
  readonly filled: number;
  readonly total: number;
}) {
  const percent = completionPercent(filled, total);
  const tone = percent >= 100 ? 'ok' : percent >= 50 ? 'warn' : 'bad';
  return (
    <span className="op-meter" data-testid="op-completion">
      <span
        className={`op-meter-track tone-${tone}`}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={filled}
        aria-valuetext={`${filled} من ${total} حقول مطلوبة`}
      >
        <span className="op-meter-fill" style={{ inlineSize: `${percent}%` }} />
      </span>
      <span className="tiny op-meter-label">
        {filled}/{total}
      </span>
    </span>
  );
}
